import React from 'react';
import { useSelector } from 'react-redux';
import {
  Ad,
  AppContent,
  baseUnit,
  ErrorBoundary,
  SecondarySectionTitle,
  Section,
  SectionHeader,
  SectionOnTopTitle,
  SectionWithTitle,
  Sponsor,
  ThreeVCExpandable,
} from 'mm-ui-components';
import { Store } from 'redux';
import { css, StyleSheet } from 'aphrodite/no-important';
import { Theme } from '../../components/connectedComponents/Theme';
import { MetaTags } from '../../components/connectedComponents/MetaTags';
import { HeadScripts } from '../../components/connectedComponents/HeadScripts';
import { SECTION_NAMES, SLOT_NAMES } from '../templates.const';
import { LoadMoreButton, useFetchNextPosts } from '../../components/connectedComponents/LoadMoreButton';
import { Page } from '../Page';
import { getAdSlotId } from '../../store/config/config.selectors';
import { ErrorReporter } from '../../errorReporting/errorReporter.type';
import { AppActions, AppState } from '../../store/initialStateRegistration';
import { useMeasureHydrateTime } from '../useMeasureHydrateTime';
import {
  getLogo,
  getMetadataDescription,
  getMetadataTitle,
  getSectionArticlesByKey,
  getSectionImage,
  getSectionSubtitle,
  getSectionTitleByKey,
  getShowMorePaginationTextByKey,
  getShowMorePaginationURLByKey,
  getSponsoredText,
  getMetadataImage,
} from '../../store/template/feedPage/feedPageOrlando/feedPageOrlando.selectors';
import { DefaultNavigationLayout } from '../../components/connectedComponents/DefaultNavigationLayout';

interface FeedPageBangkokProps {
  store: Store<AppState, AppActions>;
  errorProvider: ErrorReporter;
}

const getArticlesInTemplate = (state: AppState) => {
  const feedSectionArticles = getSectionArticlesByKey(SECTION_NAMES.FEED_SECTION)(state) || [];
  return [
    ...feedSectionArticles,
  ];
};

export const FeedPageOrlandoComponent: React.FunctionComponent<FeedPageBangkokProps> = ({ store, errorProvider }) => {
  useMeasureHydrateTime();
  return (
    <Page store={store} errorProvider={errorProvider}>
      <ErrorBoundary>
        <FeedPageOrlandoContentConnected />
      </ErrorBoundary>
    </Page>
  );
};

const createStyle = () => StyleSheet.create({
  sectionsWrapper: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gridRowGap: `${baseUnit}px`,
    marginBottom: `${baseUnit * 0.5}px`,
    justifyItems: 'center',
  },
});

export const FeedPageOrlandoContentConnected: React.FunctionComponent = () => {
  const cardsSectionDisplayName = useSelector(getSectionTitleByKey(SECTION_NAMES.FEED_SECTION));
  const topSectionDisplayName = useSelector(getSectionTitleByKey(SECTION_NAMES.TOP_SECTION));
  const topSectionSubtitle = useSelector(getSectionSubtitle(SECTION_NAMES.TOP_SECTION));
  const topSectionImage = useSelector(getSectionImage(SECTION_NAMES.TOP_SECTION));
  const articles = useSelector(getArticlesInTemplate);
  const cardsSectionArticles = useSelector(getSectionArticlesByKey(SECTION_NAMES.FEED_SECTION));
  const logo = useSelector(getLogo);
  const sponsorText = useSelector(getSponsoredText);
  const oneOnOneId = useSelector(getAdSlotId(SLOT_NAMES.ONE_ON_ONE));
  const metaTitle = useSelector(getMetadataTitle);
  const metaDescription = useSelector(getMetadataDescription);
  const pageImage = useSelector(getMetadataImage);

  const style = createStyle();
  return (
    <React.Fragment>
      <MetaTags articles={articles} title={metaTitle} description={metaDescription} pageImage={pageImage} />
      <HeadScripts />
      <Theme>
        <AppContent>
          <DefaultNavigationLayout mainWrapperConfigThemeKey="mainContent">
            <div className={css(style.sectionsWrapper)}>

              <Section themeConfigurationPropKey="firstSectionMaxWidth">
                <SectionOnTopTitle title={topSectionDisplayName} subtitle={topSectionSubtitle} image={topSectionImage} />
              </Section>

              <Sponsor brandName={logo.alt} text={sponsorText} logo={logo.src} href={logo.href} />

              <SectionWithTitle>
                <SectionHeader
                  moreButtonSvg={null}
                  moreButtonText={null}
                  paddingFactors={{ large: 1, medium: 0.5, small: 1 }}
                >
                  <SecondarySectionTitle>{cardsSectionDisplayName}</SecondarySectionTitle>
                </SectionHeader>
                <ThreeVCExpandable cards={cardsSectionArticles} paddingFactors={{ large: 1, medium: 0.5, small: 1 }} />
              </SectionWithTitle>

            </div>
            <LoadMoreFeedPostsButtonConnected />
            <Ad id={oneOnOneId} />
          </DefaultNavigationLayout>
        </AppContent>
      </Theme>
    </React.Fragment>
  );
};

export const LoadMoreFeedPostsButtonConnected: React.FunctionComponent = () => {
  const sectionKey = SECTION_NAMES.FEED_SECTION;
  const shouldDisplay = !!useSelector(getShowMorePaginationURLByKey(sectionKey));
  const text = useSelector(getShowMorePaginationTextByKey(sectionKey));
  const onClick = useFetchNextPosts(sectionKey, getShowMorePaginationURLByKey);
  return <LoadMoreButton {...{ onClick, shouldDisplay, text }} />;
};
